import { createFileRoute } from "@tanstack/react-router";
import { CircleDashed, Clock, ClipboardList } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { TableSkeleton } from "@/components/shared/Skeletons";
import PageHeader from "@/components/shared/PageHeader";
import EmptyState from "@/components/shared/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAssessments } from "@/hooks/useAssessment";
import { formatDate } from "@/utils/formatters";

export const Route = createFileRoute("/student/assessments/")({
  component: AssessmentsPage,
});

function AssessmentsPage() {
  const { user } = useAuthStore();
  const { data: assessments, isLoading } = useAssessments();

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  const assessmentList = (assessments as any[]) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "published": return "success";
      case "draft": return "outline";
      case "closed": return "destructive";
      default: return "outline";
    }
  };

  return (
    <div>
      <PageHeader
        title="My assessments"
        subtitle="View and complete assessments to demonstrate your learning progress"
      />

      {assessmentList.length > 0 && (
        <ul className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {assessmentList.map((assessment: any) => (
            <li
              key={assessment.id}
              className="flex h-full flex-col rounded-md border border-border bg-card p-4 md:p-5"
            >
              <div className="mb-4 flex items-start justify-between gap-2">
                <Badge variant="info" className="capitalize">
                  {assessment.type || "quiz"}
                </Badge>
                <Badge variant={getStatusColor(assessment.status)} dot className="capitalize">
                  {assessment.status}
                </Badge>
              </div>

              <h3 className="mb-1 text-[15px] font-medium text-foreground">{assessment.title}</h3>
              <p className="mb-4 text-sm text-muted-foreground">{assessment.description}</p>

              <dl className="mb-4 space-y-1.5 text-caption text-muted-foreground">
                <div className="flex justify-between gap-2">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="size-3.5" aria-hidden />
                    Duration: {assessment.duration || 30} min
                  </span>
                  <span className="tabular-nums">Marks: {assessment.total_marks || 100}</span>
                </div>
                {assessment.module && <div>Module: {assessment.module}</div>}
                {assessment.due_date && (
                  <div className="text-warning">Due: {formatDate(assessment.due_date)}</div>
                )}
              </dl>

              <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-4">
                <Badge variant="outline">
                  <CircleDashed aria-hidden />
                  Not started
                </Badge>
                <Button size="sm">Start assessment</Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {assessmentList.length === 0 && (
        <EmptyState
          icon={<ClipboardList />}
          title="No assessments available yet"
          description="Check back later or contact your institution"
        />
      )}
    </div>
  );
}
